import React from 'react';
import { createRoot } from 'react-dom/client';
import * as ReactRouterDOM from 'react-router-dom';
import App from './App';
import { AuthProvider } from './contexts/AuthContext';
import './styles/globals.css';
import './i18n'; // Initialize i18n
import ErrorBoundary from './components/common/ErrorBoundary';
import { ThemeProvider } from './contexts/ThemeContext';
import { initializeCriticalServices, initializeDeferredServices } from './services/initServices';
import { ServiceProvider } from './contexts/ServiceContext';
import { webServiceRegistry } from './services/serviceRegistry';
import Spinner from './components/common/Spinner';
import { ThemeMigrationService } from './services/ThemeMigrationService';
import LoggerService from './services/loggerService';

/**
 * Bootstrap function to initialize the application.
 * PERFORMANCE OPTIMIZED:
 * 1. Renders a lightweight skeleton immediately (Time to First Paint)
 * 2. Initializes critical services (Config) in parallel/cached
 * 3. Renders full application
 * 4. Initializes deferred services (Sentry, Tracing) in background
 */
async function bootstrap() {
  // Execute legacy theme preference migration before starting up
  ThemeMigrationService.executeMigration();

  const rootElement = document.getElementById('root');
  if (!rootElement) {
    throw new Error('Could not find root element to mount to');
  }

  const root = createRoot(rootElement);

  // 1. Render Skeleton / Loading UI immediately
  root.render(
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', backgroundColor: '#f9fafb' }}>
      <Spinner size="xl" />
      <p style={{ marginTop: '1rem', color: '#6b7280', fontFamily: 'system-ui, sans-serif' }}>Loading application...</p>
    </div>
  );

  try {
    // 2. Initialize critical services (Configuration)
    // If cached, this returns almost immediately
    const result = await initializeCriticalServices();

    if (!result.success) {
      throw result.error || new Error('Critical service initialization failed');
    }

    // 3. Render React application
    root.render(
      <React.StrictMode>
        <ServiceProvider services={webServiceRegistry}>
          <ReactRouterDOM.BrowserRouter
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <ThemeProvider>
              <AuthProvider>
                <ErrorBoundary>
                  <App />
                </ErrorBoundary>
              </AuthProvider>
            </ThemeProvider>
          </ReactRouterDOM.BrowserRouter>
        </ServiceProvider>
      </React.StrictMode>
    );

    LoggerService.info('Application bootstrapped successfully');

    // 4. Initialize non-critical services in background
    initializeDeferredServices();

  } catch (error) {
    LoggerService.error('Fatal error during application bootstrap:', error);

    // Display user-friendly error message safely
    // We strictly use textContent for dynamic error data to prevent XSS
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';

    rootElement.innerHTML = `
        <div style="display: flex; align-items: center; justify-content: center; height: 100vh; font-family: system-ui, sans-serif; background-color: #f9fafb;">
            <div style="text-align: center; max-width: 500px; padding: 2rem; background: white; border-radius: 0.5rem; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);">
                <h1 style="color: #ef4444; margin-bottom: 1rem; font-size: 1.5rem; font-weight: 600;">Failed to Start Application</h1>
                <p style="color: #4b5563; margin-bottom: 1rem; line-height: 1.5;">
                    The application failed to initialize configuration. Please check your internet connection and try again.
                </p>
                <button 
                    onclick="window.location.reload()" 
                    style="background: #3b82f6; color: white; padding: 0.75rem 1.5rem; border: none; border-radius: 0.375rem; cursor: pointer; font-weight: 500; transition: background 0.2s;"
                >
                    Reload Page
                </button>
                <div id="error-details" style="color: #9ca3af; font-size: 0.875rem; margin-top: 1.5rem; font-family: monospace;"></div>
            </div>
        </div>
    `;

    const errorDetails = document.getElementById('error-details');
    if (errorDetails) {
      errorDetails.textContent = `Error: ${errorMessage}`;
    }
  }
}

// Start the bootstrap process
bootstrap();
